import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './TopologyPanel.css';

/**
 * TopologyPanel — vue dédiée aux insights topologiques du graphe.
 *
 * Affiche 3 blocs :
 *  - Hubs : nœuds hautement connectés (centralité)
 *  - Critiques sans tests : nœuds avec dépendances entrantes mais pas de tests
 *  - Isolés : nœuds sans aucune relation
 *
 * Props:
 *  - topology       {Object}   { hubNodes, untested, isolatedNodes, nodeCount, relationCount }
 *  - onNodeSelect   {Function} (nodeName) → sélectionne le nœud dans le graph
 *  - onOpenTests    {Function} (node)     → ouvre la section Tests pré-sélectionnée
 *  - onOpenRelations {Function}(node)     → ouvre la section Relations pré-sélectionnée
 *  - onOpenImpactRadar {Function}(node)  → lance l'Impact Radar sur ce nœud
 */
export default function TopologyPanel({ topology, onNodeSelect, onOpenTests, onOpenRelations, onOpenImpactRadar }) {
  const { t } = useTranslation();
  const [expandedSection, setExpandedSection] = useState('hubs'); // 'hubs' | 'untested' | 'isolated' | null

  if (!topology) {
    return (
      <div className="topology-panel topology-panel--empty">
        <p>{t('topologyPanel.noData', "Aucune donnée topologique disponible. Lancez une analyse depuis l'Insight Engine.")}</p>
      </div>
    );
  }

  const { hubNodes = [], untested = [], isolatedNodes = [], nodeCount = 0, relationCount = 0 } = topology;

  const toggle = (key) => setExpandedSection(prev => prev === key ? null : key);

  const densityRatio = nodeCount > 0 ? (relationCount / nodeCount).toFixed(1) : 0;

  return (
    <div className="topology-panel">
      {/* ── Stats globales ─────────────────────────────────────────── */}
      <div className="topology-panel__stats">
        <div className="topo-stat-card">
          <span className="topo-stat-card__value">{nodeCount}</span>
          <span className="topo-stat-card__label">{t('topologyPanel.stats.nodes', 'Nœuds')}</span>
        </div>
        <div className="topo-stat-card">
          <span className="topo-stat-card__value">{relationCount}</span>
          <span className="topo-stat-card__label">{t('topologyPanel.stats.relations', 'Relations')}</span>
        </div>
        <div className="topo-stat-card">
          <span className="topo-stat-card__value">{densityRatio}</span>
          <span className="topo-stat-card__label">{t('topologyPanel.stats.density', 'Relations / Nœud')}</span>
        </div>
        <div className={`topo-stat-card ${hubNodes.length > 0 ? 'topo-stat-card--warn' : ''}`}>
          <span className="topo-stat-card__value">{hubNodes.length}</span>
          <span className="topo-stat-card__label">{t('topologyPanel.stats.hubs', 'Hubs')}</span>
        </div>
        <div className={`topo-stat-card ${untested.length > 0 ? 'topo-stat-card--danger' : 'topo-stat-card--ok'}`}>
          <span className="topo-stat-card__value">{untested.length}</span>
          <span className="topo-stat-card__label">{t('topologyPanel.stats.untested', 'Critiques sans tests')}</span>
        </div>
        <div className={`topo-stat-card ${isolatedNodes.length > 0 ? 'topo-stat-card--muted' : 'topo-stat-card--ok'}`}>
          <span className="topo-stat-card__value">{isolatedNodes.length}</span>
          <span className="topo-stat-card__label">{t('topologyPanel.stats.isolated', 'Isolés')}</span>
        </div>
      </div>

      {/* ── Bloc 1 : Hubs ─────────────────────────────────────────── */}
      <div className={`topology-block topology-block--hubs ${expandedSection === 'hubs' ? 'topology-block--open' : ''}`}>
        <button
          className="topology-block__header"
          onClick={() => toggle('hubs')}
          aria-expanded={expandedSection === 'hubs'}
        >
          <span className="topology-block__icon">🔗</span>
          <div className="topology-block__header-text">
            <strong>{t('topologyPanel.hubs.title', 'Nœuds hautement connectés (Hubs)')}</strong>
            <span className="topology-block__count">{hubNodes.length} nœud{hubNodes.length !== 1 ? 's' : ''}</span>
          </div>
          <span className="topology-block__chevron">{expandedSection === 'hubs' ? '▲' : '▼'}</span>
        </button>

        {expandedSection === 'hubs' && (
          <div className="topology-block__body">
            <p className="topology-block__desc">
              {t('topologyPanel.hubs.desc', 'Ces nœuds centralisent beaucoup de dépendances. Un changement ici peut avoir un impact en cascade. Analysez leur impact avant modification.')}
            </p>
            {hubNodes.length === 0 ? (
              <p className="topology-block__empty">{t('topologyPanel.hubs.empty', 'Aucun hub détecté (tous les nœuds ont ≤ 2 connexions).')}</p>
            ) : (
              <table className="topology-table">
                <thead>
                  <tr>
                    <th>{t('topologyPanel.table.node', 'Nœud')}</th>
                    <th>{t('topologyPanel.table.type', 'Type')}</th>
                    <th className="topology-table__num">{t('topologyPanel.table.degree', 'Connexions')}</th>
                    <th>{t('topologyPanel.table.actions', 'Actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {hubNodes.map((node) => (
                    <tr key={node.name} className="topology-row">
                      <td>
                        <button
                          className="topology-row__name-btn"
                          onClick={() => onNodeSelect && onNodeSelect(node.name)}
                          title={t('topologyPanel.selectNode', 'Sélectionner dans le graphe')}
                        >
                          {node.name}
                        </button>
                      </td>
                      <td><span className="topology-type-badge">{node.type || '—'}</span></td>
                      <td className="topology-table__num">
                        <span className="topology-degree">{node.degree}</span>
                      </td>
                      <td className="topology-row__actions">
                        {onOpenImpactRadar && (
                          <button
                            className="topo-action-btn topo-action-btn--radar"
                            onClick={() => onOpenImpactRadar(node)}
                            title={t('topologyPanel.impactRadar', 'Analyser l\'impact')}
                          >
                            📡 Impact
                          </button>
                        )}
                        {onOpenTests && (
                          <button
                            className="topo-action-btn topo-action-btn--tests"
                            onClick={() => onOpenTests(node)}
                            title={t('topologyPanel.openTests', 'Voir les tests')}
                          >
                            🧪 Tests
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {/* ── Bloc 2 : Critiques sans tests ─────────────────────────── */}
      <div className={`topology-block topology-block--untested ${expandedSection === 'untested' ? 'topology-block--open' : ''}`}>
        <button
          className="topology-block__header"
          onClick={() => toggle('untested')}
          aria-expanded={expandedSection === 'untested'}
        >
          <span className="topology-block__icon">⚠️</span>
          <div className="topology-block__header-text">
            <strong>{t('topologyPanel.untested.title', 'Nœuds critiques sans tests')}</strong>
            <span className="topology-block__count">{untested.length} nœud{untested.length !== 1 ? 's' : ''}</span>
          </div>
          <span className="topology-block__chevron">{expandedSection === 'untested' ? '▲' : '▼'}</span>
        </button>

        {expandedSection === 'untested' && (
          <div className="topology-block__body">
            <p className="topology-block__desc">
              {t('topologyPanel.untested.desc', 'Ces nœuds ont des dépendances entrantes (d\'autres nœuds dépendent d\'eux) mais ne possèdent aucun test. Risque élevé en cas de régression.')}
            </p>
            {untested.length === 0 ? (
              <p className="topology-block__empty topology-block__empty--ok">✅ {t('topologyPanel.untested.empty', 'Tous les nœuds critiques ont des tests !')}</p>
            ) : (
              <table className="topology-table">
                <thead>
                  <tr>
                    <th>{t('topologyPanel.table.node', 'Nœud')}</th>
                    <th>{t('topologyPanel.table.type', 'Type')}</th>
                    <th className="topology-table__num">{t('topologyPanel.table.inDegree', 'Dépendances entrantes')}</th>
                    <th>{t('topologyPanel.table.actions', 'Actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {untested.map((node) => (
                    <tr key={node.name} className="topology-row topology-row--danger">
                      <td>
                        <button
                          className="topology-row__name-btn"
                          onClick={() => onNodeSelect && onNodeSelect(node.name)}
                          title={t('topologyPanel.selectNode', 'Sélectionner dans le graphe')}
                        >
                          {node.name}
                        </button>
                      </td>
                      <td><span className="topology-type-badge">{node.type || '—'}</span></td>
                      <td className="topology-table__num">
                        <span className="topology-degree topology-degree--danger">{node.inDegree}</span>
                      </td>
                      <td className="topology-row__actions">
                        {onOpenTests && (
                          <button
                            className="topo-action-btn topo-action-btn--tests topo-action-btn--primary"
                            onClick={() => onOpenTests(node)}
                            title={t('topologyPanel.generateTests', 'Générer des tests')}
                          >
                            🧪 Créer tests
                          </button>
                        )}
                        {onOpenImpactRadar && (
                          <button
                            className="topo-action-btn topo-action-btn--radar"
                            onClick={() => onOpenImpactRadar(node)}
                            title={t('topologyPanel.impactRadar', 'Analyser l\'impact')}
                          >
                            📡 Impact
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {/* ── Bloc 3 : Isolés ────────────────────────────────────────── */}
      <div className={`topology-block topology-block--isolated ${expandedSection === 'isolated' ? 'topology-block--open' : ''}`}>
        <button
          className="topology-block__header"
          onClick={() => toggle('isolated')}
          aria-expanded={expandedSection === 'isolated'}
        >
          <span className="topology-block__icon">🪟</span>
          <div className="topology-block__header-text">
            <strong>{t('topologyPanel.isolated.title', 'Nœuds isolés')}</strong>
            <span className="topology-block__count">{isolatedNodes.length} nœud{isolatedNodes.length !== 1 ? 's' : ''}</span>
          </div>
          <span className="topology-block__chevron">{expandedSection === 'isolated' ? '▲' : '▼'}</span>
        </button>

        {expandedSection === 'isolated' && (
          <div className="topology-block__body">
            <p className="topology-block__desc">
              {t('topologyPanel.isolated.desc', 'Ces nœuds n\'ont aucune relation. Ils peuvent être des orphelins ou des nœuds en cours de création. Connectez-les ou supprimez-les.')}
            </p>
            {isolatedNodes.length === 0 ? (
              <p className="topology-block__empty topology-block__empty--ok">✅ {t('topologyPanel.isolated.empty', 'Aucun nœud isolé — le graphe est bien connecté !')}</p>
            ) : (
              <table className="topology-table">
                <thead>
                  <tr>
                    <th>{t('topologyPanel.table.node', 'Nœud')}</th>
                    <th>{t('topologyPanel.table.type', 'Type')}</th>
                    <th>{t('topologyPanel.table.actions', 'Actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {isolatedNodes.map((node) => (
                    <tr key={node.name} className="topology-row topology-row--muted">
                      <td>
                        <button
                          className="topology-row__name-btn"
                          onClick={() => onNodeSelect && onNodeSelect(node.name)}
                          title={t('topologyPanel.selectNode', 'Sélectionner dans le graphe')}
                        >
                          {node.name}
                        </button>
                      </td>
                      <td><span className="topology-type-badge">{node.type || '—'}</span></td>
                      <td className="topology-row__actions">
                        {onOpenRelations && (
                          <button
                            className="topo-action-btn topo-action-btn--relations topo-action-btn--primary"
                            onClick={() => onOpenRelations(node)}
                            title={t('topologyPanel.addRelation', 'Ajouter une relation')}
                          >
                            🔗 Connecter
                          </button>
                        )}
                        {onOpenTests && (
                          <button
                            className="topo-action-btn topo-action-btn--tests"
                            onClick={() => onOpenTests(node)}
                          >
                            🧪 Tests
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
